<template>
  <div>
    <div class="q-mx-md container">
      <div
        ref="timeline"
        class="player row q-mx-md items-center"
      >
        <div
          v-for="clip in formattedClips"
          :key="clip.id"
          class="clip-border bg-clip-tile"
          :style="clipItemStyle(clip)"
        />
        <ClipDragger
          v-if="!boundariesDisabled"
          class="predominant-slider"
          :position-x="startTimePosition"
          @on-drag="onDragStartTime"
        >
          <div class="dragger-line" />
        </ClipDragger>
        <ClipDragger
          v-if="!boundariesDisabled"
          :position-x="endTimePosition"
          @on-drag="onDragEndTime"
        >
          <div class="dragger-line" />
        </ClipDragger>
        <ClipDragger
          is-marker
          :position-x="currentTimePosition"
          @on-drag="onDragCurrentTime"
        >
          <div class="timeline-marker-circle" />
        </ClipDragger>
        <div
          v-if="!boundariesDisabled"
          class="clip-highlight-zone"
          :style="clipHighlightDimensions"
        />
        <div class="timeline-marker start" />
        <div class="timeline-marker end" />
      </div>
    </div>
  </div>
</template>

<script>
import ClipDragger from "shared/components/players/ClipDragger.vue";
import { toMilliseconds } from "shared/helpers/date";

const CLIP_LENGTH_IN_MILLISECONDS = 30000;
// slightly over 20mins for rounding purposes
const MAX_SLIDER_DURATION = 1201000;
const SLIDER_WIDTH_OFFSET = 5;

export default {
  name: "ClipRangeTimeline",
  components: {
    ClipDragger,
  },
  props: {
    timelineCurrentTime: {
      type: Number,
      required: true,
    },
    clips: {
      type: Array,
      required: true,
    },
    rangeStartTime: {
      type: Number,
      default: 0,
    },
    rangeEndTime: {
      type: Number,
      default: 0,
    },
    timezone: {
      type: String,
      default: "",
    },
    boundariesDisabled: {
      type: Boolean,
    },
    unlimitedDuration: {
      type: Boolean,
    },
  },
  emits: [
    "on-range-start-dropped",
    "on-range-end-dropped",
    "update-timeline-current-time",
  ],
  data() {
    return {
      startTime: 0,
      endTime: 0,
      timelineClientWidth: 0,
    };
  },
  computed: {
    formattedClips() {
      const timelineDuration = this.boundaryEndTime - this.boundaryStartTime;

      return this.clips.map((clip, index) => {
        const start = toMilliseconds(clip.start_time) - this.boundaryStartTime;
        let end;
        const nextClip = this.clips[index + 1];

        if (
          nextClip &&
          toMilliseconds(nextClip.start_time) - toMilliseconds(clip.end_time) <
            CLIP_LENGTH_IN_MILLISECONDS
        ) {
          end = toMilliseconds(nextClip.start_time) - this.boundaryStartTime;
        } else {
          end = toMilliseconds(clip.end_time) - this.boundaryStartTime;
        }

        return {
          ...clip,
          left: 100 * (start / timelineDuration),
          width: 100 * ((end - start) / timelineDuration),
        };
      });
    },
    boundaryStartTime() {
      return toMilliseconds(this.clips[0].start_time);
    },
    boundaryEndTime() {
      const lastClipStartTime = this.clips[this.clips.length - 1].start_time;

      return toMilliseconds(lastClipStartTime);
    },
    startTimePosition() {
      return this.timeToPosition(this.startTime);
    },
    endTimePosition() {
      return this.timeToPosition(this.endTime);
    },
    currentTimePosition() {
      return this.timeToPosition(this.timelineCurrentTime);
    },
    clipHighlightDimensions() {
      return {
        left: `${this.startTimePosition + SLIDER_WIDTH_OFFSET}px`,
        width: `${
          this.endTimePosition - this.startTimePosition - SLIDER_WIDTH_OFFSET
        }px`,
      };
    },
  },
  watch: {
    rangeStartTime(newRangeStartTime) {
      this.startTime = toMilliseconds(newRangeStartTime);
    },
    rangeEndTime(newRangeEndTime) {
      this.endTime = toMilliseconds(newRangeEndTime);
    },
  },
  mounted() {
    this.setInitialStartAndEndTime();
    window.addEventListener("resize", this.setWidth);
    this.setWidth();
  },
  unmounted() {
    window.removeEventListener("resize", this.setWidth);
  },
  methods: {
    setInitialStartAndEndTime() {
      this.startTime = toMilliseconds(
        this.rangeStartTime || this.clips[0].start_time
      );

      this.endTime = toMilliseconds(
        this.rangeEndTime || this.clips[this.clips.length - 1].start_time
      );
    },
    setWidth() {
      this.timelineClientWidth = this.$refs.timeline.clientWidth;
    },
    positionToTime(position) {
      return this.scaleBetween(
        position,
        this.boundaryStartTime,
        this.boundaryEndTime,
        0,
        this.timelineClientWidth
      );
    },
    timeToPosition(time) {
      return this.scaleBetween(
        time,
        0,
        this.timelineClientWidth,
        this.boundaryStartTime,
        this.boundaryEndTime
      );
    },
    keepInTimelineRange(xCoordinate) {
      if (xCoordinate < 0) {
        return 0;
      }

      return xCoordinate > this.timelineClientWidth
        ? this.timelineClientWidth
        : xCoordinate;
    },
    scaleBetween(
      unscaledNumber,
      minCustomRange,
      maxCustomRange,
      minRange,
      maxRange
    ) {
      return (
        ((maxCustomRange - minCustomRange) * (unscaledNumber - minRange)) /
          (maxRange - minRange) +
        minCustomRange
      );
    },
    onDragStartTime({ x: positionX }) {
      const constrainedPosition = this.keepInTimelineRange(positionX);

      if (
        this.hasStartSliderExceededMaxDuration(constrainedPosition) ||
        this.hasStartSliderOverlappedWithEndSlider(constrainedPosition)
      ) {
        return;
      }

      this.startTime = this.positionToTime(constrainedPosition);
      this.$emit("on-range-start-dropped", this.startTime);
    },
    onDragEndTime({ x: positionX }) {
      const constrainedPosition = this.keepInTimelineRange(positionX);

      if (
        this.hasEndSliderExceededMaxDuration(constrainedPosition) ||
        this.hasEndSliderOverlappedWithStartSlider(constrainedPosition)
      ) {
        return;
      }

      this.endTime = this.positionToTime(constrainedPosition);
      this.$emit("on-range-end-dropped", this.endTime);
    },
    onDragCurrentTime({ x: positionX }) {
      const constrainedPosition = this.keepInTimelineRange(positionX);
      const constrainedTimePosition = this.positionToTime(constrainedPosition);
      this.$emit("update-timeline-current-time", constrainedTimePosition);
    },
    clipItemStyle(clip) {
      // Less than 100% because otherwise the clips it can't fit wrap
      const doesClipExceedWrappableRange = clip.left > 99.8;

      return {
        width: `${clip.width}%`,
        left: `${clip.left}%`,
        height: "15px",
        display: doesClipExceedWrappableRange ? "none" : "block",
      };
    },
    hasStartSliderExceededMaxDuration(startPosition) {
      if (this.unlimitedDuration) return false;

      return (
        this.endTime - this.positionToTime(startPosition) >= MAX_SLIDER_DURATION
      );
    },
    hasEndSliderExceededMaxDuration(endPosition) {
      if (this.unlimitedDuration) return false;

      return (
        this.positionToTime(endPosition) - this.startTime >= MAX_SLIDER_DURATION
      );
    },
    hasStartSliderOverlappedWithEndSlider(startPosition) {
      return this.endTime - this.positionToTime(startPosition) <= 0;
    },
    hasEndSliderOverlappedWithStartSlider(endPosition) {
      return this.positionToTime(endPosition) - this.startTime <= 0;
    },
  },
};
</script>

<style lang="scss" scoped>
.container {
  position: relative;
  background-color: $ds-denim-10;
}

.player {
  position: relative;
  margin: 0 auto;
  height: 100px;

  .clip-border {
    border: 1px solid $ds-denim-10;
  }
}

.timeline-marker {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  transition: all 0.2s ease-in-out;
  margin-top: 30px;
  margin-bottom: 30px;

  &.start {
    left: 0%;
    background: $clip-tile;
    z-index: 0;
  }

  &.end {
    left: 100%;
    background: $clip-tile;
    z-index: 0;
  }
}

.timeline-marker-circle {
  position: absolute;
  top: -12px;
  left: -5px;
  background-color: $white;
  height: 12px;
  width: 12px;
  border-radius: 50%;
}

.predominant-slider {
  z-index: 2;
}

.timezone-date-offset {
  margin-top: -20px;
}

.clip-highlight-zone {
  background-color: $priority-medium;
  opacity: 0.5;
  position: absolute;
  height: 40px;
}
</style>
